<template>
  <div class="bz-area">
    <!-- IMP: the design's 4 tabs. IFP stays a single states+metros list. -->
    <div v-if="product === 'IMP'" class="bz-minitabs wide">
      <button :class="{ on: tab === 'standard' }" type="button" @click="switchTab('standard')">States &amp; metros</button>
      <button :class="{ on: tab === 'zip' }" type="button" @click="switchTab('zip')">ZIP codes</button>
      <button :class="{ on: tab === 'county' }" type="button" @click="switchTab('county')">Counties</button>
      <button :class="{ on: tab === 'radius' }" type="button" @click="switchTab('radius')">
        <i class="material-icons" style="font-size:14px;line-height:1;">my_location</i>Radius
      </button>
    </div>

    <template v-if="product === 'IFP' || tab !== 'radius'">
      <div class="bz-search sm">
        <i class="material-icons" style="font-size:16px;line-height:1;color:var(--bzc-faint);">search</i>
        <input v-model="q" :placeholder="searchPlaceholder" aria-label="Search market areas" />
      </div>

      <div v-if="loadingAreas" class="bz-empty">Loading market areas…</div>

      <!-- ===== states & metros ===== -->
      <div v-else-if="product === 'IFP' || tab === 'standard'" class="bz-area-list">
        <button class="bz-area-row us" :class="{ sel: value && value.kind === 'us' }" type="button"
          @click="pick({ kind: 'us', label: 'All US' })">
          <span class="bz-dot ok"></span>
          <span class="bz-area-name">All US</span>
        </button>

        <template v-for="s in visibleStates">
          <div :key="'st' + s.id" class="bz-area-row state"
            :class="{ sel: value && value.kind === 'state' && value.stateId === s.id, off: !s.available }"
            @click="rowClickState(s)">
            <button class="bz-caret" :class="{ none: !s.expandable }" type="button"
              @click.stop="s.expandable && toggleState(s)">
              <i v-if="s.expandable" class="material-icons" style="font-size:16px;line-height:1;">
                {{ isStateOpen(s) ? 'expand_more' : 'chevron_right' }}
              </i>
            </button>
            <span class="bz-dot" :class="s.available ? 'ok' : 'no'"></span>
            <span class="bz-area-name">{{ s.name }}</span>
            <span v-if="s.meta" class="bz-area-meta">{{ s.meta }}</span>
            <span v-else-if="!s.available" class="bz-area-meta">not offered</span>
            <button v-if="s.expandable && s.available" class="bz-use" type="button"
              @click.stop="pick({ kind: 'state', stateId: s.id, label: s.name })">Select state</button>
          </div>
          <template v-if="isStateOpen(s)">
            <div v-if="s.subLoading" :key="'ml' + s.id" class="bz-area-row metro" style="cursor:default;">
              <span class="bz-area-name" style="color:var(--bzc-faint);font-style:italic;">Loading metros…</span>
            </div>
            <div v-else-if="s.subLoaded && !s.visibleMetros.length" :key="'me' + s.id" class="bz-deadend" style="padding-left:56px;">
              No metro-level data for this state yet — metros appear here as the data refresh completes. You can still select the state itself.
            </div>
            <button v-for="m in s.visibleMetros" :key="'m' + m.id" type="button"
              class="bz-area-row metro" :class="{ sel: value && value.kind === 'metro' && value.metroId === m.id, off: !m.available }"
              :disabled="!m.available"
              @click="pick({ kind: 'metro', stateId: s.id, metroId: m.id, label: m.name })">
              <span class="bz-dot" :class="m.available ? 'ok' : 'no'"></span>
              <span class="bz-area-name">{{ m.name }}</span>
              <span v-if="!m.available" class="bz-area-meta">not offered</span>
            </button>
          </template>
        </template>
      </div>

      <!-- ===== ZIP codes (multi-select) / Counties (single-select), drill by state ===== -->
      <div v-else class="bz-area-list">
        <!-- typed 5-digit ZIP quick add -->
        <button v-if="tab === 'zip' && typedZip" class="bz-area-row us" type="button" @click="toggleZip(typedZip)">
          <i class="material-icons" style="font-size:16px;line-height:1;color:var(--bzc-navy);">
            {{ zipSelected(typedZip) ? 'check_box' : 'add_box' }}
          </i>
          <span class="bz-area-name">{{ zipSelected(typedZip) ? 'Remove' : 'Add' }} ZIP {{ typedZip }}</span>
        </button>

        <template v-for="s in visibleStates">
          <div :key="'s2' + s.id" class="bz-area-row state" @click="toggleState(s)">
            <button class="bz-caret" type="button" @click.stop="toggleState(s)">
              <i class="material-icons" style="font-size:16px;line-height:1;">
                {{ isStateOpen(s) ? 'expand_more' : 'chevron_right' }}
              </i>
            </button>
            <span class="bz-area-name">{{ s.name }}</span>
          </div>
          <template v-if="isStateOpen(s)">
            <div v-if="s.subLoading" :key="'sl' + s.id" class="bz-area-row metro" style="cursor:default;">
              <span class="bz-area-name" style="color:var(--bzc-faint);font-style:italic;">Loading…</span>
            </div>
            <template v-else-if="tab === 'zip'">
              <button v-for="z in s.visibleZips" :key="'z' + s.id + z" type="button"
                class="bz-area-row metro" :class="{ sel: zipSelected(z) }" @click="toggleZip(z)">
                <i class="material-icons" style="font-size:16px;line-height:1;"
                  :style="{ color: zipSelected(z) ? 'var(--bzc-navy)' : 'var(--bzc-faint)' }">
                  {{ zipSelected(z) ? 'check_box' : 'check_box_outline_blank' }}
                </i>
                <span class="bz-area-name">{{ z }}</span>
              </button>
            </template>
            <template v-else>
              <button v-for="c in s.visibleCounties" :key="'c' + s.id + c.id" type="button"
                class="bz-area-row metro" :class="{ sel: value && value.kind === 'county' && value.countyId === c.id }"
                @click="pick({ kind: 'county', countyId: c.id, stateId: s.id, label: c.name + ' — ' + s.name })">
                <span class="bz-area-name">{{ c.name }}</span>
              </button>
            </template>
          </template>
        </template>
      </div>

      <!-- ZIP apply bar (the only multi-select area type) -->
      <div v-if="tab === 'zip' && zipSel.length" class="bz-zip-apply">
        <span>{{ zipSel.length }} ZIP{{ zipSel.length === 1 ? '' : 's' }} selected · {{ zipSel.slice(0, 4).join(', ') }}{{ zipSel.length > 4 ? ' …' : '' }}</span>
        <button class="bz-btn primary sm" type="button" @click="applyZips">
          Use {{ zipSel.length === 1 ? 'this ZIP' : 'these ' + zipSel.length + ' ZIPs' }}
        </button>
      </div>
    </template>

    <!-- ===== radius (IMP only) ===== -->
    <div v-else class="bz-radius">
      <label class="bz-field"><span>Address or ZIP</span>
        <input v-model="addr" placeholder="e.g. 17601 or 100 Main St, Lancaster PA" />
      </label>
      <label class="bz-field"><span>Radius — {{ radiusMiles }} miles</span>
        <input type="range" min="5" max="100" step="5" v-model.number="radiusMiles" />
      </label>
      <radius-map :address="addr" :miles="radiusMiles" />
      <button class="bz-btn ghost sm" type="button" :disabled="!addr.trim()" @click="pickRadius">
        <i class="material-icons" style="font-size:15px;line-height:1;">check</i>Use this radius
      </button>
    </div>
  </div>
</template>

<script>
// Availability is REAL and pre-dumped: IFP dims from /info/areas?type=if;
// IMP renders the industry-scoped USMR/LMR responses directly (no-data areas
// hidden). ZIP/county lists come from the same per-state response (they are
// geography, not industry-scoped). ZIP is the only multi-select area type.
import { fetchIfAreaUniverse, fetchIfAreaAvailability, fetchImrStates, fetchImrStateAreas } from './model';
import RadiusMap from './RadiusMap.vue';

export default {
  name: 'area-step',
  components: { RadiusMap },
  props: {
    product: { type: String, required: true },
    industry: { type: Object, default: null },
    value: { type: Object, default: null },
    // Prospect List / report variants browse the same geography but availability
    // semantics differ (or come from the live count instead) — skip the dots.
    noAvailability: { type: Boolean, default: false },
  },
  data() {
    return {
      q: '',
      tab: 'standard',
      expanded: {},
      universe: null, // IFP: {states:[{id,name,metros:[{id,name}]}]}
      avail: null, // IFP: {stateSet, metroSet}
      imrStates: null, // IMP: [{id,name,estabs}]
      imrAreas: {}, // IMP: stateId -> {metros, counties, zips} (lazy per-state)
      imrAreaLoading: {}, // IMP: stateId -> true while fetching
      zipSel: [], // ZIP multi-select working set
      loadingAreas: false,
      addr: '',
      radiusMiles: 25,
    };
  },
  computed: {
    searchPlaceholder() {
      if (this.tab === 'zip') return 'Search states, or type a 5-digit ZIP code…';
      if (this.tab === 'county') return 'Search states & counties…';
      return this.product === 'IMP' ? 'Search states & metros…' : 'Search states & metros…';
    },
    typedZip() {
      const t = this.q.trim();
      return /^\d{5}$/.test(t) ? t : null;
    },
    visibleStates() {
      const ql = this.q.trim().toLowerCase();
      const imp = this.product === 'IMP';
      // IMP standard tab hides no-data states (the scoped response IS the list);
      // ZIP/county tabs browse the full geography (they're not industry-scoped),
      // so those use the IF universe for state names.
      const geoTabs = imp && this.tab !== 'standard';
      const source = imp && !geoTabs
        ? (this.imrStates || []).map((s) => ({ id: s.id, name: s.name, estabs: s.estabs, metros: [] }))
        : (this.universe ? this.universe.states : []);
      return source
        .map((s) => {
          const available = imp ? true : (this.avail ? !!this.avail.stateSet[s.id] : true);
          const sub = this.imrAreas[s.id] || null;
          const metros = imp
            ? ((sub && sub.metros) || [])
            : s.metros.map((m) => ({
                ...m,
                available: this.avail ? !!this.avail.metroSet[m.id] : true,
              }));
          const counties = (sub && sub.counties) || [];
          const zips = (sub && sub.zips) || [];
          const metroMatches = ql ? metros.filter((m) => m.name.toLowerCase().includes(ql)) : metros;
          const countyMatches = ql ? counties.filter((c) => c.name.toLowerCase().includes(ql)) : counties;
          const zipMatches = ql ? zips.filter((z) => z.indexOf(ql) === 0) : zips;
          const stateMatches = !ql || s.name.toLowerCase().includes(ql);
          if (this.tab === 'county' && !stateMatches && !countyMatches.length) return null;
          if (this.tab === 'zip' && !stateMatches && !zipMatches.length) return null;
          if ((this.tab === 'standard' || !imp) && !stateMatches && !metroMatches.length) return null;
          return {
            id: s.id,
            name: s.name,
            available,
            metros,
            expandable: imp ? true : s.metros.length > 0,
            subLoading: !!this.imrAreaLoading[s.id],
            subLoaded: imp ? !!sub : true,
            visibleMetros: ql && !stateMatches ? metroMatches : metros,
            visibleCounties: ql && !stateMatches ? countyMatches : counties,
            visibleZips: ql && !stateMatches ? zipMatches : zips,
            searchForcedOpen: !!ql && !stateMatches,
            meta: imp && !geoTabs ? s.estabs : null,
          };
        })
        .filter(Boolean);
    },
  },
  watch: {
    industry: {
      immediate: true,
      handler() {
        this.loadAreas();
      },
    },
  },
  methods: {
    switchTab(t) {
      this.tab = t;
      this.q = '';
      // geo tabs need the state-name universe (cheap + cached)
      if ((t === 'zip' || t === 'county') && !this.universe) {
        fetchIfAreaUniverse().then((u) => { this.universe = u; }).catch(() => {});
      }
    },
    isStateOpen(s) {
      return s.searchForcedOpen || !!this.expanded[this.tab + s.id];
    },
    toggleState(s) {
      const key = this.tab + s.id;
      this.$set(this.expanded, key, !this.expanded[key]);
      // per-state sub-areas (metros/counties/zips) come in one scoped call
      if (this.product === 'IMP' && this.expanded[key] && !this.imrAreas[s.id] && !this.imrAreaLoading[s.id]) {
        this.loadImrAreas(s.id);
      }
    },
    async loadImrAreas(stateId) {
      this.$set(this.imrAreaLoading, stateId, true);
      try {
        const areas = await fetchImrStateAreas(this.industry.code, stateId);
        this.$set(this.imrAreas, stateId, areas);
      } catch (e) {
        this.$set(this.imrAreas, stateId, { metros: [], counties: [], zips: [] });
      } finally {
        this.$set(this.imrAreaLoading, stateId, false);
      }
    },
    rowClickState(s) {
      if (s.expandable) this.toggleState(s);
      else if (s.available) this.pick({ kind: 'state', stateId: s.id, label: s.name });
    },
    pick(area) {
      this.$emit('change', area);
    },
    /* ---- ZIP multi-select ---- */
    zipSelected(z) {
      return this.zipSel.indexOf(z) !== -1;
    },
    toggleZip(z) {
      const i = this.zipSel.indexOf(z);
      if (i === -1) this.zipSel.push(z);
      else this.zipSel.splice(i, 1);
    },
    applyZips() {
      const zips = this.zipSel.slice();
      const label = zips.length === 1 ? 'ZIP ' + zips[0] : zips.length + ' ZIPs · ' + zips[0] + ' +' + (zips.length - 1);
      this.pick({ kind: 'zip', zips, label });
    },
    pickRadius() {
      const addr = this.addr.trim();
      const zip = /^\d{5}$/.test(addr) ? addr : '';
      this.pick({
        kind: 'radius',
        label: addr + ' · ' + this.radiusMiles + ' mi',
        radius: { address: addr, zip, miles: this.radiusMiles },
      });
    },
    async loadAreas() {
      // Prospect List (IFP + noAvailability) browses the industry-independent geography
      // universe with all areas enabled, so it can load before an industry is picked
      // (7.18 #6a: drop the industry-first requirement). IMP and availability-scoped IFP
      // still need the industry first (their geography is industry-scoped).
      if (!this.industry && !(this.product === 'IFP' && this.noAvailability)) return;
      try {
        if (this.product === 'IMP') {
          // IMP: the industry-scoped states response is the whole list
          // (pre-dumped USMR, with-data states only) — no universe needed.
          this.loadingAreas = true;
          this.imrStates = null;
          this.imrAreas = {};
          this.zipSel = [];
          this.imrStates = await fetchImrStates(this.industry.code);
        } else {
          this.loadingAreas = !this.universe;
          this.universe = await fetchIfAreaUniverse();
          if (this.noAvailability) {
            this.avail = null;
          } else {
            this.avail = await fetchIfAreaAvailability(this.industry.code);
          }
        }
      } catch (e) {
        // degrade gracefully: universe without availability = everything enabled
        if (this.product === 'IFP') this.avail = null;
        else this.imrStates = null;
      } finally {
        this.loadingAreas = false;
      }
    },
  },
};
</script>
